import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Loader2, Sparkles, CheckCircle, Lock } from 'lucide-react'
import type { Season, Registration, SeasonBracket } from './types'
import { formatDate, formatCurrency } from './utils'

interface UpcomingSeasonCalloutProps {
  season: Season
  brackets: SeasonBracket[]
  myRegistration: Registration | null
  isAlreadyInSeason: boolean
  registrationLoading: boolean
  registrationSubmitting: boolean
  isMember: boolean
  onOpenRegistration: () => void
  onCancelRegistration: () => void
}

export function UpcomingSeasonCallout({
  season,
  brackets,
  myRegistration,
  isAlreadyInSeason,
  registrationLoading,
  registrationSubmitting,
  isMember,
  onOpenRegistration,
  onCancelRegistration,
}: UpcomingSeasonCalloutProps) {
  const canRegister = season.registrationMode === 'anyone' || isMember

  return (
    <Card className="mb-6 border-emerald-500/30 bg-gradient-to-r from-emerald-500/5 to-emerald-500/10">
      <CardContent className="py-4 space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-emerald-500/10 p-2 shrink-0">
              {isAlreadyInSeason || myRegistration ? (
                <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              ) : (
                <Sparkles className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              )}
            </div>
            <div>
              {isAlreadyInSeason ? (
                <>
                  <p className="font-medium">You're already in {season.name}</p>
                  <p className="text-sm text-muted-foreground">
                    Starts {formatDate(season.startDate)}
                  </p>
                </>
              ) : myRegistration ? (
                <>
                  <p className="font-medium">
                    {myRegistration.status === 'pending' && 'Your registration is pending approval'}
                    {myRegistration.status === 'confirmed' && 'You are registered for ' + season.name}
                    {myRegistration.status === 'rejected' && 'Your registration was not accepted'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Starts {formatDate(season.startDate)}
                  </p>
                </>
              ) : (
                <>
                  <p className="font-medium">{season.name} registration is open!</p>
                  <p className="text-sm text-muted-foreground">
                    Starts {formatDate(season.startDate)}
                    {season.joiningFee && (
                      <span className="ml-2 font-medium text-emerald-700 dark:text-emerald-400">
                        • {formatCurrency(season.joiningFee.amount, season.joiningFee.currency)}
                      </span>
                    )}
                    {brackets.length > 0 && (
                      <span className="ml-2">
                        • {brackets.length} bracket{brackets.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </p>
                </>
              )}
            </div>
          </div>
          {(() => {
            if (registrationLoading) {
              return <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            }
            if (isAlreadyInSeason) {
              return <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">✓ You're in</span>
            }
            if (myRegistration?.status === 'pending') {
              return (
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={onCancelRegistration}
                  disabled={registrationSubmitting}
                  className="w-full sm:w-auto"
                >
                  {registrationSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                  Cancel Registration
                </Button>
              )
            }
            if (myRegistration?.status === 'confirmed') {
              return <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">✓ Confirmed</span>
            }
            if (myRegistration?.status === 'rejected' && canRegister) {
              return (
                <Button 
                  size="sm" 
                  className="bg-emerald-600 hover:bg-emerald-700 text-white w-full sm:w-auto"
                  onClick={onOpenRegistration}
                >
                  Try Again
                </Button>
              )
            }
            if (canRegister) {
              return (
                <Button 
                  size="sm" 
                  className="bg-emerald-600 hover:bg-emerald-700 text-white w-full sm:w-auto"
                  onClick={onOpenRegistration}
                >
                  Register Interest
                </Button>
              )
            }
            return (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Lock className="h-3 w-3" />
                Members only
              </span>
            )
          })()}
        </div>
      </CardContent>
    </Card>
  )
}
